// Template paste-import (Jun 27) — DEV-only.
//
// Figma's plugin sandbox blocks "Send to dev" (http://localhost fetch from
// the plugin iframe = mixed content / sandbox). This page sidesteps it
// entirely: it runs in the BROWSER at localhost:3000, so its POST to
// /api/figma-templates-save is SAME-ORIGIN — no mixed content, no CORS, no
// Figma network sandbox.
//
// Workflow: in the plugin, tick "Text overlay" → Copy JSON → paste here →
// Save. Lands in text-templates/<id>.json (same writer the plugin's
// Send-to-dev would have used). For text-templates dest the canvas
// background is stripped (mirrors the plugin's text-overlay transform), so
// it's safe even if you copied without ticking the checkbox.

import { useCallback, useMemo, useState } from 'react';

type Dest = 'text-templates' | 'json-templates';
type SaveState = { ok?: boolean; path?: string; error?: string } | null;

export default function ImportTemplatePage() {
  const [json, setJson] = useState('');
  const [idOverride, setIdOverride] = useState('');
  const [dest, setDest] = useState<Dest>('text-templates');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<SaveState>(null);

  // Live-parse so we can show the id + element count before saving.
  const parsed = useMemo(() => {
    if (!json.trim()) return null;
    try {
      return JSON.parse(json) as { id?: string; name?: string; canvas?: unknown; elements?: unknown[] };
    } catch {
      return undefined; // undefined = invalid JSON (vs null = empty)
    }
  }, [json]);

  const save = useCallback(async () => {
    setResult(null);
    if (!json.trim()) {
      setResult({ error: 'Paste a template JSON first.' });
      return;
    }
    let tpl: Record<string, unknown>;
    try {
      tpl = JSON.parse(json);
    } catch (e) {
      setResult({ error: `Invalid JSON: ${e instanceof Error ? e.message : String(e)}` });
      return;
    }
    // id override (lets you rename "untitled-xxxx" → "text-template-2").
    if (idOverride.trim()) tpl.id = idOverride.trim();
    // text-templates: strip the canvas bg so the fusion scene shows through
    // (mirrors the plugin's "Text overlay" transform — idempotent).
    if (dest === 'text-templates' && tpl.canvas && typeof tpl.canvas === 'object') {
      const c = tpl.canvas as Record<string, unknown>;
      delete c.backgroundColor;
      delete c.backgroundImage;
    }

    setSaving(true);
    try {
      // Same-origin POST — this page is served by the Vite dev server.
      const resp = await fetch('/api/figma-templates-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template: tpl, newFonts: [], dest }),
      });
      const body = (await resp.json()) as SaveState;
      if (!resp.ok || !body?.ok) {
        setResult({ error: body?.error || `Server returned ${resp.status}.` });
      } else {
        setResult(body);
      }
    } catch (e) {
      setResult({ error: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }, [json, idOverride, dest]);

  return (
    <div className="min-h-screen bg-surface text-dark px-6 py-6 max-w-[840px] mx-auto">
      <h1 className="text-lg font-bold">Import template (paste)</h1>
      <p className="text-xs text-dark-muted mt-1">
        Figma "Send to dev" is blocked by the plugin sandbox (mixed content). Paste the
        plugin's <b>Copy JSON</b> here instead — same-origin, always works.
      </p>

      <div className="flex items-center gap-4 mt-4 text-xs flex-wrap">
        <span className="font-semibold">Save to:</span>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="radio" checked={dest === 'text-templates'} onChange={() => setDest('text-templates')} />
          text-templates/ <span className="text-dark-muted/70">(overlay — bg stripped)</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="radio" checked={dest === 'json-templates'} onChange={() => setDest('json-templates')} />
          json-templates/ <span className="text-dark-muted/70">(social post)</span>
        </label>
      </div>

      <label className="flex items-center gap-2 mt-3 text-xs">
        <span className="font-semibold">id override (optional):</span>
        <input
          value={idOverride}
          onChange={(e) => setIdOverride(e.target.value)}
          placeholder="e.g. text-template-2 (kebab-case)"
          spellCheck={false}
          className="border border-surface-dark/40 rounded px-2 py-1 font-mono w-[280px] bg-white"
        />
      </label>

      <textarea
        value={json}
        onChange={(e) => setJson(e.target.value)}
        placeholder="Paste the template JSON here…"
        spellCheck={false}
        className="w-full h-[340px] mt-3 border border-surface-dark/40 rounded p-3 font-mono text-[11px] bg-white"
      />

      <div className="flex items-center gap-3 mt-3">
        <button
          onClick={() => void save()}
          disabled={saving || parsed === undefined || !json.trim()}
          className="bg-primary text-white text-sm font-semibold px-5 py-2 rounded disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save template'}
        </button>
        {parsed === undefined && <span className="text-xs text-red-600">Invalid JSON</span>}
        {parsed && (
          <span className="text-xs text-dark-muted">
            id: <b>{idOverride.trim() || parsed.id || '(none)'}</b> ·{' '}
            {Array.isArray(parsed.elements) ? parsed.elements.length : 0} elements
          </span>
        )}
      </div>

      {result?.ok && (
        <p className="text-xs text-emerald-700 mt-3 break-words">Saved → {result.path}</p>
      )}
      {result?.error && <p className="text-xs text-red-600 mt-3 break-words">{result.error}</p>}
    </div>
  );
}
